import { useEffect, useMemo, useState } from 'react'
import { legacyWorkScheduleMemos } from '../../mock/legacyWorkScheduleList'
import './LegacyWorkScheduleMemo.css'

type MemoAction = 'save' | 'modify' | 'delete'

type WorkScheduleMemoOpener = Window & {
  lf_loadData?: (showSpinner: boolean, memoDate: string, officeId: string, officeName: string) => void
}

const plainText = (html: string) => html
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/<\/p>/gi, '\n')
  .replace(/<[^>]+>/g, '')
  .replace(/\n+$/, '')

function initialMemo(storageKey: string, edit: boolean, memoDate: string) {
  if (!edit) return ''
  const defaultMemo = legacyWorkScheduleMemos[memoDate] ?? Object.values(legacyWorkScheduleMemos)[0] ?? ''
  try {
    return window.sessionStorage.getItem(storageKey) ?? plainText(defaultMemo)
  } catch {
    return plainText(defaultMemo)
  }
}

export function LegacyWorkScheduleMemo() {
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const crud = params.get('CRUD') ?? '1'
  const memoDate = params.get('memo_date') ?? '2026-08-29'
  const officeId = params.get('office_id') ?? 'mock-office'
  const officeName = params.get('office_name') ?? 'Mock 지점'
  const memoId = params.get('memo_id') ?? 'mock-memo'
  const edit = crud !== '1'
  const storageKey = `legacy-s03-work-memo:${officeId}:${memoDate}`
  const [content, setContent] = useState(() => initialMemo(storageKey, edit, memoDate))

  useEffect(() => {
    const previousTitle = document.title
    document.title = '업무일지 메모'
    return () => { document.title = previousTitle }
  }, [])

  const finish = (action: MemoAction) => {
    const actionLabel = action === 'save' ? '저장' : action === 'modify' ? '수정' : '삭제'
    if (!window.confirm(`${memoDate} 일자 메모를 ${actionLabel}하시겠습니까?`)) return

    const nextContent = action === 'delete' ? '' : content
    try {
      window.sessionStorage.setItem(storageKey, nextContent)
    } catch {
      // Storage is an optional convenience for the local Mock popup.
    }

    const detail = {
      type: 'legacy-work-schedule-memo-change',
      action,
      memoDate,
      officeId,
      officeName,
      memoId,
      content: nextContent,
    }
    const opener = window.opener as WorkScheduleMemoOpener | null
    if (opener && !opener.closed) {
      try {
        opener.postMessage(detail, window.location.origin)
      } catch {
        // A differently hosted opener must not receive local Mock state.
      }
      try {
        opener.dispatchEvent(new CustomEvent('legacy-work-schedule-memo-change', { detail }))
      } catch {
        // postMessage above remains the cross-window Mock refresh signal.
      }
      try {
        opener.lf_loadData?.(false, memoDate, officeId, officeName)
      } catch {
        // The React opener refreshes through the event; the Django callback is optional.
      }
    }
    window.close()
  }

  return <main className="legacy-s03-memo-main">
    <div className="legacy-s03-memo-wrap">
      <div hidden aria-hidden="true">
        <input id="l_memo_date" value={memoDate} readOnly />
        <input id="l_office_id" value={officeId} readOnly />
        <input id="l_office_name" value={officeName} readOnly />
        <input id="l_staff_id" value="mock-staff" readOnly />
        <input id="l_memo_id" value={memoId} readOnly />
      </div>
      <label htmlFor="id_memo_content">메모내용</label>
      <div className="note-editor">
        <div className="note-toolbar" aria-label="메모 편집 도구">
          <button type="button" aria-label="글꼴">글꼴⌄</button>
          <button type="button" aria-label="굵게"><b>B</b></button>
          <button type="button" aria-label="밑줄"><u>U</u></button>
          <button type="button" aria-label="글자색">A</button>
          <button type="button" aria-label="글자 크기">크기⌄</button>
          <button type="button" aria-label="스타일">●</button>
          <button type="button" aria-label="스타일 선택">⌄</button>
          <button type="button" aria-label="목록">≡</button>
          <button type="button" aria-label="정렬">≡</button>
          <button type="button" aria-label="단락">단락⌄</button>
          <button type="button" aria-label="표">▦⌄</button>
          <button type="button" aria-label="실행 취소">↶</button>
          <button type="button" aria-label="다시 실행">↷</button>
          <button type="button" aria-label="확대">□</button>
          <button type="button" aria-label="전체 화면">⛶</button>
          <button type="button" aria-label="코드 보기">&lt;/&gt;</button>
          <button type="button" aria-label="도움말">?</button>
        </div>
        <textarea id="id_memo_content" value={content} onChange={(event) => setContent(event.target.value)} />
      </div>
      <div className="legacy-s03-memo-actions">
        {edit ? <>
          <button id="l_memo_modify_btn" className="g_btn modify" type="button" onClick={() => finish('modify')}>메모 수정</button>
          <button id="l_memo_delete_btn" className="g_btn delete" type="button" onClick={() => finish('delete')}>메모 삭제</button>
        </> : <button id="l_memo_save_btn" className="g_btn save" type="button" onClick={() => finish('save')}>메모 저장</button>}
      </div>
    </div>
  </main>
}
